/**
 * Bildwerte aus dem Kontrakt.
 *
 * Seit der Auslagerung des Profilbilds (Viewer-Performance Stufe 1) liefert der
 * Kontrakt für `vcard.profileImage` eine **URL** statt einer `data:`-URI. Für
 * eine Anzeige über `<img src>` ist das gleichwertig — für alles, was die
 * **Bytes** braucht, nicht. Genau daran hing die `.vcf`-Datei.
 */

const BASE64_MARKER = ';base64,'

/** Ist der Wert bereits eine fertige Quelle für `<img src>`? */
const istQuelle = (value: string): boolean =>
  value.startsWith('data:') ||
  value.startsWith('http://') ||
  value.startsWith('https://') ||
  value.startsWith('/')

/**
 * Macht aus einem Kontrakt-Bildwert eine Quelle für `<img src>`.
 *
 * Verträgt beide Formen: die URL aus dem heutigen Kontrakt und das rohe base64
 * aus dem Altbestand. Ohne diese Unterscheidung entstünde aus einer URL das
 * Unding `data:image/png;base64,https://…`.
 */
export function toImageSrc(value?: string | null): string {
  if (!value) return ''
  return istQuelle(value) ? value : `data:image/png;base64,${value}`
}

/**
 * Liefert die **rohen base64-Bytes** eines Bildwerts — ohne `data:`-Prefix.
 *
 * Gebraucht für die `.vcf`-Datei: Das vCard-Format bettet das Foto als base64
 * ein. Stünde dort die URL, enthielte die heruntergeladene Kontaktdatei statt
 * des Bildes eine Zeichenkette — das Foto im Adressbuch wäre weg, und zwar
 * lautlos: Die Datei lädt weiterhin, sie ist nur ohne Bild.
 *
 * Der Abruf ist in aller Regel ein Treffer im Browser-Cache, weil dasselbe Bild
 * unmittelbar davor schon als `<img>` geladen wurde.
 *
 * Schlägt er fehl, gibt die Funktion einen leeren String zurück statt zu
 * werfen: Eine Kontaktdatei ohne Foto ist brauchbar, eine fehlgeschlagene
 * Schaltfläche nicht.
 */
export async function toRawBase64(value?: string | null): Promise<string> {
  if (!value) return ''

  if (!istQuelle(value)) return value

  if (value.startsWith('data:')) {
    const idx = value.indexOf(BASE64_MARKER)
    return idx >= 0 ? value.slice(idx + BASE64_MARKER.length) : ''
  }

  try {
    const res = await fetch(value)
    if (!res.ok) return ''
    const blob = await res.blob()
    const dataUri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
    const idx = dataUri.indexOf(BASE64_MARKER)
    return idx >= 0 ? dataUri.slice(idx + BASE64_MARKER.length) : ''
  } catch {
    return ''
  }
}
